import React, { useEffect, useRef, useState } from "react";

// Vidéo de fond qui démarre seule, y compris dans les navigateurs intégrés
// d'Instagram et TikTok — d'où vient l'essentiel du trafic.
//
// ⚠️ Quatre pièges, tous rencontrés en vrai le 07/09/2026 :
//
// 1. React pose `muted` comme PROPRIÉTÉ du nœud, pas comme attribut HTML.
//    iOS regarde l'ATTRIBUT pour décider si la lecture automatique est
//    autorisée. Sans lui, il bloque et affiche un gros bouton play au centre.
//
// 2. `play()` renvoie une promesse qui peut être rejetée. C'est justement ce
//    rejet qui nous dit, tout de suite, que la lecture est bloquée.
//
// 3. Le mode économie d'énergie d'iOS coupe la lecture automatique et aucune
//    API JavaScript ne l'expose. On ne cherche donc pas à le détecter : on
//    réagit au symptôme, ce qui couvre aussi l'économiseur de données et les
//    politiques de navigateur.
//
// 4. ⚠️ NE JAMAIS masquer la vidéo (opacity 0, display none, hors écran) en
//    attendant qu'elle démarre. Chrome met en pause les vidéos SANS PISTE
//    AUDIO qui ne sont pas visibles, pour économiser la batterie :
//      « video-only background media was paused to save power »
//    Nos vidéos sont muettes et sans piste audio depuis la compression, donc
//    la règle s'applique. Les masquer empêche la lecture qu'on cherche
//    justement à obtenir.
//
// 5. La même règle s'applique à un ONGLET EN ARRIÈRE-PLAN — cas courant quand
//    on ouvre un lien dans un nouvel onglet depuis Instagram. Il ne faut donc
//    surtout pas conclure au blocage tant que la page est cachée : on
//    attendrait, et on retente quand elle redevient visible. Sans ça, le
//    visiteur garderait l'image fixe même après être revenu sur l'onglet.
//
// STRATÉGIE : la première image du plan est posée DERRIÈRE la vidéo. Tant que
// la lecture n'a pas commencé, la vidéo affiche ce même visuel via son
// attribut `poster` — la transition est donc invisible. Si la lecture est
// refusée, on retire la balise <video> : le bouton play d'iOS ne se dessine
// que sur une balise <video>, il disparaît avec elle, et l'image fixe reste.

// Filet de sécurité pour les navigateurs qui ne rejettent pas play() mais ne
// démarrent jamais. Le cas normal est traité par le rejet, bien plus rapide.
const PLAY_CHECK_DELAY_MS = 1500;

// Le poster porte le nom de la vidéo, en .jpg, dans le même dossier. Générer
// avec : ffmpeg -i public/videos/X.mp4 -ss 1 -frames:v 1 -q:v 6 public/videos/X.jpg
function posterFor(src) {
  return src.replace(/\.mp4($|\?)/i, ".jpg$1");
}

export default function AutoplayVideo({ src, className }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [posterOk, setPosterOk] = useState(true);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;

    video.muted = true;
    video.setAttribute("muted", "");
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");

    const onPlaying = () => {
      setPlaying(true);
      setBlocked(false);
    };
    video.addEventListener("playing", onPlaying);

    // Onglet caché : la mise en pause est normale et temporaire, ce n'est pas
    // un blocage. On ne retire surtout pas la vidéo dans ce cas.
    const giveUp = () => {
      if (!document.hidden && video.paused) setBlocked(true);
    };

    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === "function") {
        // Le rejet arrive vite : c'est lui qui évite que le bouton play
        // d'iOS reste affiché le temps d'un délai.
        p.catch(giveUp);
      }
    };

    tryPlay();

    const timer = setTimeout(giveUp, PLAY_CHECK_DELAY_MS);

    // Un geste de l'utilisateur lève la plupart des blocages.
    const onGesture = () => {
      const el = ref.current;
      if (el && el.paused) tryPlay();
    };
    document.addEventListener("touchstart", onGesture, { passive: true });
    document.addEventListener("click", onGesture);

    // Retour sur l'onglet : on relance, et on remet la vidéo si elle avait
    // été retirée.
    const onVisibility = () => {
      if (document.hidden) return;
      setBlocked(false);
      const el = ref.current;
      if (el && el.paused) tryPlay();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(timer);
      video.removeEventListener("playing", onPlaying);
      document.removeEventListener("touchstart", onGesture);
      document.removeEventListener("click", onGesture);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [src]);

  const poster = posterFor(src);

  return (
    <>
      {/* Image fixe, derrière. Retirée dès que la vidéo tourne, pour ne pas se
          superposer à elle. Même classe que la vidéo, donc même cadrage, même
          position et même opacité : la bascule ne se voit pas. */}
      {!playing && posterOk && (
        <div
          className={className}
          aria-hidden="true"
          style={{
            backgroundImage: `url(${poster})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
          }}
        >
          <img
            src={poster}
            alt=""
            onError={() => setPosterOk(false)}
            style={{ display: "none" }}
          />
        </div>
      )}

      {/* La vidéo est toujours pleinement visible — voir le piège n°4. */}
      {!blocked && (
        <video
          ref={ref}
          className={className}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={poster}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
    </>
  );
}
